import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import React, { useEffect, useState } from "react";
import { twJoin } from "tailwind-merge";

import { Flow, FlowVersion } from "src/api/flowTypes";
import { useFlows } from "src/api/queries";
import { Icon } from "src/base-components/Icon";
import { LoadingView } from "src/base-components/LoadingView";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import { toastActions } from "src/design-system/Toast/utils";
import { rightSidePaneWidthClassname } from "src/flowContainer/RightPane";
import { FlowList } from "src/nodeEditor/SelectFlowPane/FlowList";
import { FlowVersionList } from "src/nodeEditor/SelectFlowPane/FlowVersionList";
import { useAuthoringContext } from "src/router/routerContextHooks";

type SelectFlowProps = {
  selectedFlow: Flow | null;
  onFlowSelect: (flow: Flow) => void;
  selectedVersionId?: string;
  onVersionSelect: (version: FlowVersion) => void;
};

export const SelectFlowAndVersion: React.FC<SelectFlowProps> = ({
  selectedFlow,
  onVersionSelect,
  selectedVersionId,
  onFlowSelect,
}) => {
  const { workspace } = useAuthoringContext();
  const flowsQuery = useFlows({
    workspaceId: workspace.id,
  });

  return (
    <>
      {selectedFlow ? (
        <FlowVersionList
          flowId={selectedFlow.id}
          flowVersions={selectedFlow.versions}
          selectedVersionId={selectedVersionId}
          onSelect={onVersionSelect}
        />
      ) : (
        <LoadingView
          queryResult={flowsQuery}
          renderUpdated={(flows: Flow[]) => (
            <FlowList flows={flows} onSelect={onFlowSelect} />
          )}
        />
      )}
    </>
  );
};

type SelectFlowPaneProps = {
  node: BeMappedNode;
  onClose: () => void;
  onSelect: (flow: Flow, version: FlowVersion) => void;
};

export const SelectFlowPane: React.FC<SelectFlowPaneProps> = ({
  node,
  onClose,
  onSelect,
}) => {
  const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);

  useEffect(() => {
    toastActions.dismiss(node.id);
  }, [node.id]);

  const handleBack = () => {
    setSelectedFlow(null);
  };

  return (
    <div
      className={twJoin(
        "flex max-h-full flex-col overflow-hidden bg-white",
        rightSidePaneWidthClassname,
      )}
    >
      <div className="flex flex-row items-center justify-between px-6 pb-2 pt-4 text-gray-800">
        <div className="flex">
          {selectedFlow && (
            <Icon
              color="text-gray-500"
              icon={faArrowLeft}
              size="xs"
              onClick={handleBack}
            />
          )}
          <h3 className="ml-2 font-inter-semibold-16px">
            {selectedFlow ? "Select a version" : "Select a Decision Flow"}
          </h3>
        </div>
        <Icon
          color="text-gray-500 hover:cursor-pointer"
          icon={faXmark}
          size="xl"
          onClick={onClose}
        />
      </div>
      <div className="overflow-hidden px-6 pb-4 pt-2">
        <SelectFlowAndVersion
          selectedFlow={selectedFlow}
          onFlowSelect={setSelectedFlow}
          onVersionSelect={(version) => {
            if (selectedFlow) {
              onSelect(selectedFlow, version);
            }
          }}
        />
      </div>
    </div>
  );
};
